import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Modal,
} from "react-native";
import { platformAlert } from "../utils/platformAlert";
import { SafeAreaView } from "react-native-safe-area-context";
import { Feather } from "@expo/vector-icons";
import { useNavigation, useRoute } from "@react-navigation/native";
import { useColors, ThemeColors, theme } from "../hooks/useColors";
import { NotificationBell } from "./NotificationBell";
import { useDeliveryAddressContext } from "../contexts/DeliveryAddressContext";
import { OrderTrackingBanner } from "./OrderTrackingBanner";
import { spacing } from "../theme";
import { useCart } from "../hooks/useCart";
import { useTranslation } from "react-i18next";
import { navigationRef } from "../services/NavigationService";
import { useGuestMode } from "../contexts/GuestModeContext";

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    safeAreaContainer: {
      backgroundColor: colors.headerBackground,
    },
    header: {
      height: 56,
      backgroundColor: colors.headerBackground,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingHorizontal: 16,
    },
    leftSide: {
      flexDirection: "row",
      alignItems: "center",
      flex: 1,
      overflow: "visible",
    },
    addressText: {
      marginLeft: 6,
      color: colors.primary,
      fontSize: 12,
      maxWidth: 150, // Limit width to prevent overflow
      fontWeight: "600",
    },
    titleContainer: {
      flex: 2,
      alignItems: "center",
    },
    title: {
      fontSize: 18,
      fontWeight: "bold",
      color: colors.headerText,
      // Dynamic color applied inline
    },
    rightSide: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "flex-end",
      flex: 1,
    },
    notificationsButton: {
      marginRight: 16,
      position: "relative",
    },
    badgeContainer: {
      position: "absolute",
      top: -6,
      right: -6,
      backgroundColor: colors.primary,
      borderRadius: 10,
      minWidth: 20,
      height: 20,
      alignItems: "center",
      justifyContent: "center",
      borderWidth: 2,
      borderColor: colors.background,
    },
    badgeText: {
      color: "#000",
      fontSize: 10,
      fontWeight: "600",
    },
    cartButton: {
      marginRight: 8,
      position: "relative",
    },
    cartBadge: {
      position: "absolute",
      top: -6,
      right: -6,
      backgroundColor: colors.primary,
      borderRadius: 10,
      minWidth: 20,
      height: 20,
      alignItems: "center",
      justifyContent: "center",
      borderWidth: 2,
      borderColor: colors.background,
    },
    cartBadgeText: {
      color: "#000",
      fontSize: 10,
      fontWeight: "600",
    },
    modalOverlay: {
      flex: 1,
      backgroundColor: colors.overlay,
      justifyContent: "center",
      alignItems: "center",
    },
    modalContent: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: spacing.lg,
      margin: spacing.lg,
      minWidth: 280,
      shadowColor: colors.shadow,
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.3,
      shadowRadius: 8,
      elevation: 8,
    },
    modalHeader: {
      flexDirection: "row",
      alignItems: "center",
      marginBottom: spacing.md,
    },
    modalTitle: {
      fontSize: 16,
      fontWeight: "600",
      color: colors.text,
      marginLeft: spacing.sm,
    },
    modalAddressText: {
      fontSize: 14,
      color: colors.text,
      lineHeight: 20,
      marginBottom: spacing.md,
    },
    modalButtons: {
      flexDirection: "row",
      justifyContent: "flex-end",
      gap: spacing.md,
    },
    modalButton: {
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
      borderRadius: 8,
      minWidth: 80,
      alignItems: "center",
    },
    changeButton: {
      backgroundColor: colors.primary,
    },
    closeButton: {
      backgroundColor: colors.backgroundSecondary,
    },
    buttonText: {
      fontSize: 14,
      fontWeight: "600",
      color: colors.surface,
    },
    closeButtonText: {
      fontSize: 14,
      fontWeight: "600",
      color: colors.text,
    },
  });

// Inner component that uses navigation hooks
// This will only be rendered when navigation is ready
function MainHeaderInner({ title }: { title: string }) {
  // These hooks will only be called when this component is rendered,
  // which should only happen when navigation is ready
  const navigation = useNavigation();
  const route = useRoute();
  const colors = useColors();
  const styles = createStyles(colors);
  const { t } = useTranslation();
  // Removed old notification context usage - now handled by NotificationBell component
  const { formattedAddress, selectedAddress } = useDeliveryAddressContext();
  const { cartItems } = useCart();
  const { requireAuth } = useGuestMode();
  const [showAddressModal, setShowAddressModal] = useState(false);
  
  const cartItemCount = cartItems.reduce((total, item) => total + item.quantity, 0);
  
  // Safe navigation helper
  const safeNavigate = (screen: string, params?: any) => {
    try {
      if (navigation) {
        navigation.navigate(screen as never, params);
      } else if (navigationRef.isReady()) {
        navigationRef.navigate(screen as any, params);
      }
    } catch (error) {
      console.warn('[MainHeader] Navigation error:', error);
    }
  };

  // Format short address (street address only)
  const formatShortAddress = (fullAddress: string): string => {
    if (!fullAddress || fullAddress === "Set Delivery Address") {
      return fullAddress;
    }

    if (selectedAddress?.street_address) {
      let shortAddress = selectedAddress.street_address;

      // Add apartment if available and short
      if (selectedAddress.apartment && selectedAddress.apartment.length <= 10) {
        shortAddress += ` ${selectedAddress.apartment}`;
      }

      // Truncate if too long
      if (shortAddress.length > 25) {
        shortAddress = shortAddress.substring(0, 22) + "...";
      }

      return shortAddress;
    }

    // Fallback: truncate the formatted address
    return fullAddress.length > 25
      ? fullAddress.substring(0, 22) + "..."
      : fullAddress;
  };

  const onAddressPress = () => {
    // Check auth - show modal if guest
    if (!requireAuth('set delivery address')) {
      return;
    }
    
    if (formattedAddress === "Set Delivery Address") {
      safeNavigate("DeliveryAddress");
    } else {
      setShowAddressModal(true);
    }
  };

  const handleChangeAddress = () => {
    setShowAddressModal(false);
    safeNavigate("DeliveryAddress");
  };
  // Notification press handled by NotificationBell component
  const onCartPress = () => {
    // Check auth - show modal if guest
    if (!requireAuth('access cart')) {
      return;
    }
    
    // Check if user has items in cart and no delivery address set
    if (cartItemCount > 0 && (!selectedAddress || formattedAddress === "Set Delivery Address")) {
      platformAlert(
        t("cart.deliveryAddressRequired"),
        t("cart.setDeliveryAddressMessage"),
        [
          {
            text: t("cart.setAddress"),
            onPress: () => {
              safeNavigate("DeliveryAddress");
            },
          },
          { text: t("cart.cancel"), style: "cancel" },
        ]
      );
      return;
    }
    
    safeNavigate("CartsByStore");
  };

  const shortAddress = formatShortAddress(formattedAddress);

  return (
    <>
      <SafeAreaView style={styles.safeAreaContainer} edges={["top"]}>
        <View style={styles.header}>
          {/* Left side (address) */}
          <TouchableOpacity onPress={onAddressPress} style={styles.leftSide}>
            <Feather name="map-pin" size={24} color={colors.primary} />
            <Text
              style={styles.addressText}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {shortAddress}
            </Text>
          </TouchableOpacity>

          {/* Center (title) */}
          <View style={styles.titleContainer}>
            <Text style={styles.title}>{title}</Text>
          </View>

          {/* Right side (notifications + cart) */}
          <View style={styles.rightSide}>
            <NotificationBell />

            <TouchableOpacity onPress={onCartPress} style={styles.cartButton}>
              <Feather name="shopping-bag" size={24} color={colors.primary} />
              {cartItemCount > 0 && (
                <View style={styles.cartBadge}>
                  <Text style={styles.cartBadgeText}>
                    {cartItemCount > 99 ? "99+" : cartItemCount.toString()}
                  </Text>
                </View>
              )}
            </TouchableOpacity>
          </View>
        </View>
        {/* Order Tracking Banner */}
        {/* <OrderTrackingBanner /> */}
      </SafeAreaView>

      {/* Address Details Modal */}
      <Modal
        visible={showAddressModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowAddressModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Feather name="map-pin" size={20} color={colors.primary} />
              <Text style={styles.modalTitle}>Delivery Address</Text>
            </View>

            <Text style={styles.modalAddressText}>{formattedAddress}</Text>

            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={[styles.modalButton, styles.closeButton]}
                onPress={() => setShowAddressModal(false)}
              >
                <Text style={styles.closeButtonText}>Close</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.modalButton, styles.changeButton]}
                onPress={handleChangeAddress}
              >
                <Text style={styles.buttonText}>Change</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </>
  );
}

// Fallback header component when navigation isn't ready
function MainHeaderFallback({ title }: { title: string }) {
  const colors = useColors();
  const styles = createStyles(colors);
  const { formattedAddress } = useDeliveryAddressContext();
  const { cartItems } = useCart();
  
  const cartItemCount = cartItems.reduce((total, item) => total + item.quantity, 0);
  const shortAddress = formattedAddress?.length > 25
    ? formattedAddress.substring(0, 22) + "..."
    : formattedAddress || "Set Delivery Address";

  return (
    <SafeAreaView style={styles.safeAreaContainer} edges={["top"]}>
      <View style={styles.header}>
        <View style={styles.leftSide}>
          <Feather name="map-pin" size={24} color={colors.primary} />
          <Text style={styles.addressText} numberOfLines={1} ellipsizeMode="tail">
            {shortAddress}
          </Text>
        </View>
        <View style={styles.titleContainer}>
          <Text style={styles.title}>{title}</Text>
        </View>
        <View style={styles.rightSide}>
          <View style={styles.cartButton}>
            <Feather name="shopping-bag" size={24} color={colors.primary} />
            {cartItemCount > 0 && (
              <View style={styles.cartBadge}>
                <Text style={styles.cartBadgeText}>
                  {cartItemCount > 99 ? "99+" : cartItemCount.toString()}
                </Text>
              </View>
            )}
          </View>
        </View>
      </View>
    </SafeAreaView>
  );
}

// Main component - only renders navigation-dependent component when ready
function MainHeader({ title }: { title: string }) {
  const [isNavigationReady, setIsNavigationReady] = useState(false);
  
  useEffect(() => {
    // Check if navigation is ready
    if (navigationRef.isReady()) {
      setIsNavigationReady(true);
      return;
    }
    
    // Poll until navigation is ready
    const checkInterval = setInterval(() => {
      if (navigationRef.isReady()) {
        setIsNavigationReady(true);
        clearInterval(checkInterval);
      }
    }, 100);
    
    return () => clearInterval(checkInterval);
  }, []);
  
  // Show fallback until navigation is ready
  if (!isNavigationReady) {
    return <MainHeaderFallback title={title} />;
  }
  
  // Once ready, render the component that uses hooks
  return <MainHeaderInner title={title} />;
}

export default MainHeader;
